import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PostListItem from "@/components/feature/PostListItem";
import { posts } from "@/mocks/posts";
import { formatCount } from "@/utils/format";

export default function ArticleDetail() {
  const { id } = useParams();
  const post = posts.find((item) => item.id === id);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!post) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <SiteHeader />
        <div className="w-full px-4 md:px-8 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
            这口瓜好像被吃完了
          </h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的内容不存在或已被移除。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap hover:bg-primary-600"
          >
            回到首页继续吃瓜
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const related = posts.filter((item) => item.id !== post.id).slice(0, 4);
  const hotList = [...posts].sort((a, b) => b.heat - a.heat).slice(0, 5);

  const handleCopy = () => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteHeader />
      <main className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link to={`/category/${post.categorySlug}`} className="hover:text-primary-600">
            {post.categoryName}
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-700 line-clamp-1">{post.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <article className="lg:col-span-8">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-fire-line" aria-hidden="true"></i>
              {post.categoryName}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold leading-snug text-foreground-950">
              {post.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-user-3-line" aria-hidden="true"></i>
                {post.author}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {post.publishedAt}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-time-line" aria-hidden="true"></i>
                阅读约 {post.readTime}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-eye-line" aria-hidden="true"></i>
                {formatCount(post.views)}
              </span>
            </div>

            <div className="mt-7 h-64 md:h-96 w-full overflow-hidden rounded-lg">
              <img
                src={post.cover}
                alt={`${post.title}相关配图`}
                title={`${post.title} - 吃瓜看料`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {post.content.map((paragraph, index) => (
                <p key={index} className="text-[15px] md:text-base leading-8 text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 whitespace-nowrap transition-colors hover:bg-secondary-100 hover:text-secondary-800"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200/70 pt-6">
              <button
                type="button"
                onClick={() => setLiked((value) => !value)}
                className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  liked
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300/70 text-foreground-800 hover:border-primary-400"
                }`}
              >
                <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} aria-hidden="true"></i>
                {liked ? "已点赞" : "点赞"} {formatCount(post.likes + (liked ? 1 : 0))}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300/70 px-4 py-2.5 text-sm font-medium text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-400 cursor-pointer"
              >
                <i className="ri-link" aria-hidden="true"></i>
                {copied ? "链接已复制" : "分享"}
              </button>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-background-100 px-4 py-2.5 text-sm text-foreground-600 whitespace-nowrap">
                <i className="ri-chat-3-line" aria-hidden="true"></i>
                {post.comments} 条讨论
              </span>
            </div>

            <div className="mt-6 rounded-lg bg-accent-100 p-4 text-sm leading-relaxed text-accent-900">
              <i className="ri-alert-line mr-1.5" aria-hidden="true"></i>
              本文为平台示例内容，仅用于展示吃瓜看料聚集地的阅读体验，不构成任何事实陈述。理性吃瓜，拒绝网络暴力。
            </div>

            <div className="mt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">
                你可能还想吃这些瓜
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group flex gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <div className="h-20 w-24 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={item.cover}
                        alt={`${item.title}相关配图`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-950 line-clamp-2 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-xs text-foreground-500">{item.categoryName}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950 mb-1">
                  <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
                  实时吃瓜热榜
                </h3>
                <div className="divide-y divide-background-200/70">
                  {hotList.map((item, index) => (
                    <PostListItem key={item.id} post={item} rank={index + 1} />
                  ))}
                </div>
              </div>
              <div className="rounded-lg bg-foreground-950 p-5">
                <h3 className="font-heading text-base font-bold text-background-50">
                  你有更猛的料？
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-background-300/80">
                  匿名提交线索，经核实后将登上首页头条。
                </p>
                <Link
                  to="/"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap hover:bg-primary-600"
                >
                  我要爆料
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}